import * as Popover from "@radix-ui/react-popover";
import { Slider } from "@components/inputs";
import {
  ArrowTrendingUpIcon,
  PencilSquareIcon,
  ShoppingCartIcon,
} from "@heroicons/react/20/solid";
import { PriceStatus } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { FormattedPrice } from "../types";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "@components/loadingSpinner";
import Button from "@components/theming/button";
import { Card, Text } from "@components/theming";
import { useLocale, useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";

interface Props {
  price: FormattedPrice;
  selectedQuantities: {
    id: string;
    qty: number;
    qty_from_combo: number;
  }[];
  putQtyInCart: (
    price: FormattedPrice,
    qty: string,
    isDynamic?: boolean
  ) => Promise<"success" | "failure" | { status: string; retry_after: number }>;
}

export default function FlexiPricePopover({
  price,
  selectedQuantities,
  putQtyInCart,
}: Props) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dynamicPrice, setDynamicPrice] = useState(0);
  const t = useTranslations("EventPage.flexi");
  const isShopsite = useIsShopsite();
  const locale = useLocale();

  const selectedQty = selectedQuantities.find((sq) => sq.id === price.id);

  const option_array = [
    0,
    ...Array.from(
      {
        length:
          (price.bookingLimit ?? price.max) -
          price.min +
          (price.min === 0 ? 0 : 1) +
          (price.bookingLimit && price.bookingLimit < price.max && selectedQty
            ? selectedQty.qty
            : 0),
      },
      (_, index) => price.min + index + (price.min === 0 ? 1 : 0)
    ),
  ];

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          color="crimson"
          disabled={
            price.status !== PriceStatus.AVAILABLE && selectedQty?.qty === 0
          }
          whitelabelClasses="!gap-1"
          shopsiteClasses="bg-iticket hover:bg-iticket/90 text-white font-semibold disabled:bg-iticket/70 disabled:text-gray-100/70 disabled:cursor-not-allowed tabular-nums overflow-hidden inline-flex h-8 gap-1"
        >
          <ArrowTrendingUpIcon className="h-5 w-5" />
          {formatPrice(
            locale,
            price.currency.CurrencyCode,
            dynamicPrice ? dynamicPrice : price.price,
            true
          )}
          <PencilSquareIcon className="h-4 w-4" />
        </Button>
      </Popover.Trigger>
      <Popover.Anchor />
      <AnimatePresence>
        {open && (
          <Popover.Portal
            forceMount
            container={document.getElementById(
              isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
            )}
          >
            <Popover.Content
              forceMount
              sideOffset={5}
              align="end"
              alignOffset={-42}
              className="relative flex flex-col rounded-2xl w-72 z-10"
            >
              <Card asChild variant="classic" size="3">
                <motion.div
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={{
                    hidden: {
                      opacity: 0,
                      y: "-1rem",
                      backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
                      transition: { duration: 0.125 },
                    },
                    show: {
                      opacity: 1,
                      backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
                      y: 0,
                    },
                  }}
                  transition={{ duration: 0.25 }}
                >
                  <div
                    className={joinClassNames(
                      "relative flex flex-col gap-4 justify-between flex-1",
                      isShopsite
                        ? "px-6 pt-8 pb-4 bg-iticket rounded-t-2xl border-b-0 border border-[#be003f]"
                        : "pt-1.5"
                    )}
                  >
                    <Text
                      as="span"
                      size="5"
                      weight="bold"
                      shopsiteClasses="font-bold text-white text-xl"
                    >
                      {t("flexiprice")}
                    </Text>
                    <ArrowTrendingUpIcon
                      className={joinClassNames(
                        "absolute w-14 h-14 -rotate-3",
                        isShopsite
                          ? "text-white/10 -top-0.5 left-2"
                          : "text-[color:var(--crimson-a3)] -top-6 -left-2"
                      )}
                    />
                  </div>
                  <div
                    className={joinClassNames(
                      "flex flex-col gap-2",
                      isShopsite
                        ? "pt-6 p-4 border-t-0 border rounded-b-2xl bg-white"
                        : "pt-4"
                    )}
                  >
                    <div className="flex flex-wrap gap-2 items-center justify-center">
                      <div className="relative">
                        <select
                          value={qty}
                          onChange={(e) => setQty(parseInt(e.target.value))}
                          className={joinClassNames(
                            "relative peer pl-2 pr-6 text-sm leading-none border shadow-sm cursor-default w-fit h-9 focus:outline-none focus:ring-1 transition-colors ease-in-out duration-200 appearance-none",
                            isShopsite
                              ? "rounded text-black focus:ring-iticket bg-white border-gray-300 hover:text-iticket-dark hover:bg-iticket/10 hover:border-iticket focus:border-iticket"
                              : "rounded-[var(--radius-2)] focus:ring-[color:var(--crimson-a3)] bg-[color:var(--gray-a1)] border-[color:var(--gray-8)] hover:bg-[color:var(--crimson-a4)] hover:border-[color:var(--crimson-9)] focus:border-[color:var(--crimson-9)]"
                          )}
                        >
                          {option_array.map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                        <ChevronDownIcon
                          className={joinClassNames(
                            "absolute top-1/2 -translate-y-1/2 right-2 h-4 w-4 pointer-events-none transition-colors ease-in-out duration-200",
                            isShopsite
                              ? "text-black peer-hover:text-iticket-dark"
                              : ""
                          )}
                        />
                      </div>
                      <Text as="p">{t("ticketsfor", { count: qty })}</Text>
                    </div>
                    <div className="flex items-center justify-center my-4">
                      <Text
                        as="span"
                        weight="bold"
                        whitelabelClasses="text-[color:var(--crimson-9)]"
                        shopsiteClasses="font-black text-iticket"
                        sharedClasses="relative w-fit leading-none text-5xl"
                      >
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          dynamicPrice ? dynamicPrice : price.price,
                          true
                        )}
                        <Text
                          as="span"
                          sharedClasses="absolute top-0 left-full text-base"
                        >
                          {t("each")}
                        </Text>
                      </Text>
                    </div>
                    <Slider
                      defaultValue={[dynamicPrice ? dynamicPrice : price.price]}
                      max={price.priceMaximum}
                      min={price.priceMinimum}
                      step={1}
                      onValueChange={(val) => {
                        setDynamicPrice(val[0]);
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <Text as="span">
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          price.priceMinimum,
                          true
                        )}
                      </Text>
                      <Text as="span">
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          price.priceMaximum,
                          true
                        )}
                      </Text>
                    </div>
                    <Button
                      size="3"
                      color="crimson"
                      shopsiteVariant="iticket"
                      shopsiteClasses="gap-2"
                      onClick={async () => {
                        setLoading(true);
                        const newPrice = dynamicPrice || price.price;
                        const newObjPrice = { ...price, price: newPrice };
                        await putQtyInCart(newObjPrice, qty.toString(), true);
                        setQty(0);
                        setLoading(false);
                        setOpen(false);
                      }}
                      disabled={loading || qty === 0}
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner
                            size="20"
                            color={isShopsite ? "#ffffff" : "var(--gray-11)"}
                          />
                          <span>{t("addingtocart")}</span>
                        </>
                      ) : (
                        <>
                          <ShoppingCartIcon className="w-5 h-5" />
                          <span>{t("addtocart")}</span>
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              </Card>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}
